import { Injectable } from '@angular/core';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { AppConfigService } from './app-config.service';
import { EnvironmentService } from '../core/services/environment.service';

@Injectable({
  providedIn: 'root'
})
export class ApplicationInsightsService {
  private appInsights: ApplicationInsights | undefined;
  private isInitialized = false;

  constructor(private appConfigService: EnvironmentService) {
    // Don't initialize here - wait for app config to load
  }

  /**
   * Initialize Application Insights after config is loaded
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    try {
      this.appInsights = new ApplicationInsights({
        config: {
          connectionString: this.appConfigService.getEnvironment().applicationInsights.connectionString,
          enableAutoRouteTracking: true, // Track route changes automatically
          enableRequestHeaderTracking: true,
          enableResponseHeaderTracking: true,
          enableAjaxErrorStatusText: true,
          enableAjaxPerfTracking: true,
          maxAjaxCallsPerView: 20,
          disableExceptionTracking: false,
          disableTelemetry: false,
          enableCorsCorrelation: true,
          distributedTracingMode: 1, // DistributedTracingModes.AI_AND_W3C
          enableUnhandledPromiseRejectionTracking: true
        }
      });

      this.appInsights.loadAppInsights();
      this.appInsights.trackPageView(); // Track initial page load
      this.isInitialized = true;
    } catch (error) {
      console.error('Failed to initialize Application Insights:', error);
    }
  }

  /**
   * Track a custom event
   */
  trackEvent(name: string, properties?: { [key: string]: any }, measurements?: { [key: string]: number }): void {
    if (!this.appInsights) {
      console.warn('Application Insights not initialized. Skipping event tracking.');
      return;
    }
    this.appInsights.trackEvent({ 
      name, 
      properties, 
      measurements 
    });
  }

  /**
   * Track a page view
   */
  trackPageView(name?: string, uri?: string, properties?: { [key: string]: any }, measurements?: { [key: string]: number }): void {
    if (!this.appInsights) {
      console.warn('Application Insights not initialized. Skipping page view tracking.');
      return;
    }
    this.appInsights.trackPageView({ 
      name, 
      uri, 
      properties, 
      measurements 
    });
  }

  /**
   * Track an exception
   */
  trackException(exception: Error, severityLevel?: number, properties?: { [key: string]: any }, measurements?: { [key: string]: number }): void {
    if (!this.appInsights) {
      console.warn('Application Insights not initialized. Skipping exception tracking.');
      return;
    }
    this.appInsights.trackException({ 
      exception,
      severityLevel,
      properties,
      measurements
    });
  }

  /**
   * Track a custom metric
   */
  trackMetric(name: string, average: number, sampleCount?: number, min?: number, max?: number, properties?: { [key: string]: any }): void {
    if (!this.appInsights) {
      console.warn('Application Insights not initialized. Skipping metric tracking.');
      return;
    }
    this.appInsights.trackMetric({ 
      name, 
      average, 
      sampleCount, 
      min, 
      max 
    }, properties);
  }

  /**
   * Set user context
   */
  setAuthenticatedUserContext(authenticatedUserId: string, accountId?: string): void {
    if (!this.appInsights) {
      console.warn('Application Insights not initialized. Skipping user context setting.');
      return;
    }
    this.appInsights.setAuthenticatedUserContext(authenticatedUserId, accountId);
  }

  /**
   * Clear user context
   */
  clearAuthenticatedUserContext(): void {
    if (!this.appInsights) {
      console.warn('Application Insights not initialized. Skipping user context clearing.');
      return;
    }
    this.appInsights.clearAuthenticatedUserContext();
  }

  /**
   * Flush telemetry
   */
  flush(): void {
    if (!this.appInsights) {
      console.warn('Application Insights not initialized. Skipping flush.');
      return;
    }
    this.appInsights.flush();
  }

  /**
   * Get the underlying Application Insights instance
   */
  getAppInsights(): ApplicationInsights | undefined {
    return this.appInsights;
  }
}
